import React, { useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../firebase";
import Popup from "./Popup";
import Confirm from "./Confirm";
import EditTeamDetails from "./EditTeamDetails";
import EditTeamPlayer from "./EditTeamPlayer";

const TeamDetails = ({ gameId, teamId }) => {
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [captain, setCaptain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [popup, setPopup] = useState({ isOpen: false, message: '', title: '', type: 'info' });
  const [confirm, setConfirm] = useState({ isOpen: false, message: '', title: '', type: 'warning', onConfirm: null });
  const [stats, setStats] = useState({
    totalSpent: 0,
    averagePrice: 0,
    highestBuy: 0,
    lowestBuy: 0,
  });
  useEffect(() => {
    if (!gameId || !teamId) return;
    setLoading(true);
    // Listen to team details
    const teamRef = ref(db, `games/${gameId}/teams/${teamId}`);
    const unsubscribeTeam = onValue(teamRef, (snapshot) => {
      if (snapshot.exists()) {
        const teamData = snapshot.val();
        setTeam(teamData);

        // Fetch captain info if captainUid exists
        if (teamData.captainUid) {
          const captainRef = ref(db, `users/${teamData.captainUid}`);
          onValue(captainRef, (captainSnap) => {
            if (captainSnap.exists()) {
              setCaptain(captainSnap.val());
            }
          });
        }
      }
    });

    // Listen to all players and filter by teamId
    const playersRef = ref(db, `games/${gameId}/players`);
    const unsubscribePlayers = onValue(playersRef, (snapshot) => {
      if (snapshot.exists()) {
        const playersData = snapshot.val();
        const teamPlayers = [];
        let totalSpent = 0;
        let highest = 0;
        let lowest = Infinity;

        Object.entries(playersData).forEach(([id, player]) => {
          if (player.teamId === teamId) {
            teamPlayers.push({ ...player, id });
            const price = player.soldPrice || player.price || 0;
            totalSpent += price;
            if (price > highest) highest = price;
            if (price < lowest) lowest = price;
          }
        });

        setPlayers(teamPlayers);
        
        // Calculate stats
        setStats({
          totalSpent,
          averagePrice: teamPlayers.length > 0 ? totalSpent / teamPlayers.length : 0,
          highestBuy: highest,
          lowestBuy: lowest === Infinity ? 0 : lowest,
        });
      }
    });
    setLoading(false);
    return () => {
      unsubscribeTeam();
      unsubscribePlayers();
    };
  }, [gameId, teamId]);

  // Handle team edit
  const handleEditTeam = () => {
    setShowEditTeam(true);
  };

  const handleCloseEditTeam = () => {
    setShowEditTeam(false);
  };

  // Handle player edit
  const handleEditPlayer = (player) => {
    setEditingPlayerId(player.id);
    setEditingPlayer(player);
  };

  const handleCloseEditPlayer = () => {
    setEditingPlayerId(null);
    setEditingPlayer(null);
  };

  // Group players by role
  const playersByRole = players.reduce((acc, player) => {
    const role = player.role || "Other";
    if (!acc[role]) acc[role] = [];
    acc[role].push(player);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="alert alert-warning">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Team not found.
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Team Banner */}
      {team.teamBanner && (
        <div className="card shadow mb-4 overflow-hidden">
          <img
            src={team.teamBanner || "/banner1.jpg"}
            alt={team.teamName}
            style={{
              width: "100%",
              height: "300px",
              objectFit: "cover",
            }}
          />
        </div>
      )}

      {/* Team Header */}
      <div className="card shadow mb-4">
        <div className="card-body p-4" style={{
          background: " #667eea ",
          color: "white"
        }}>
          <div className="row align-items-center">
            <div className="col-md-8">
              <h2 className="mb-2">
                <i className="bi bi-shield-fill me-3"></i>
                {team.teamName}
              </h2>
              {captain && (
                <p className="mb-2">
                  <i className="bi bi-person-badge me-2"></i>
                  <strong>Captain:</strong> {captain.name} ({captain.email})
                </p>
              )}
              {team.captainPlayerName && (
                <p className="mb-2">
                  <i className="bi bi-star-fill me-2"></i>
                  <strong>Team Captain:</strong> {team.captainPlayerName}
                </p>
              )}
              <p className="mb-0">
                <i className="bi bi-calendar-event me-2"></i>
                <strong>Created:</strong> {new Date(team.createdAt).toLocaleString()}
              </p>
            </div>
            <div className="col-md-4 text-md-end mt-3 mt-md-0">
              <button
                className="btn btn-light mb-2 w-100"
                onClick={handleEditTeam}
              >
                <i className="bi bi-pencil-square me-2"></i>
                Edit Team Details
              </button>
              <div className="bg-white text-dark rounded p-3">
                <h5 className="mb-1">
                  <i className="bi bi-wallet2 me-2 text-success"></i>
                  Purse Left
                </h5>
                <h2 className="mb-0 text-success">৳{team.purseLeft?.toLocaleString() || 0}</h2>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Statistics */}
      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <div className="card shadow-sm h-100">
            <div className="card-body text-center">
              <i className="bi bi-people-fill text-primary fs-1"></i>
              <h6 className="mt-2 text-muted">Total Players</h6>
              <h3 className="mb-0">{players.length}</h3>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card shadow-sm h-100">
            <div className="card-body text-center">
              <i className="bi bi-cash-stack text-danger fs-1"></i>
              <h6 className="mt-2 text-muted">Total Spent</h6>
              <h3 className="mb-0">৳{stats.totalSpent.toLocaleString()}</h3>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card shadow-sm h-100">
            <div className="card-body text-center">
              <i className="bi bi-graph-up-arrow text-success fs-1"></i>
              <h6 className="mt-2 text-muted">Highest Buy</h6>
              <h3 className="mb-0">৳{stats.highestBuy.toLocaleString()}</h3>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card shadow-sm h-100">
            <div className="card-body text-center">
              <i className="bi bi-calculator text-info fs-1"></i>
              <h6 className="mt-2 text-muted">Average Price</h6>
              <h3 className="mb-0">৳{Math.round(stats.averagePrice).toLocaleString()}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Players by Role */}
      <div className="card shadow">
        <div className="card-body p-4">
          <h4 className="mb-4">
            <i className="bi bi-diagram-3-fill me-2"></i>
            Team Squad
          </h4>

          {players.length === 0 ? (
            <div className="alert alert-info text-center">
              <i className="bi bi-inbox fs-1 d-block mb-2"></i>
              No players in this team yet. Start bidding to build your squad!
            </div>
          ) : (
            <>
              {/* Role Tabs */}
              <ul className="nav nav-pills mb-4">
                <li className="nav-item">
                  <button className="nav-link active" data-bs-toggle="pill" data-bs-target="#all-players">
                    All ({players.length})
                  </button>
                </li>
                {Object.keys(playersByRole).map((role) => (
                  <li className="nav-item" key={role}>
                    <button 
                      className="nav-link" 
                      data-bs-toggle="pill" 
                      data-bs-target={`#role-${role.replace(/\s+/g, '-')}`}
                    >
                      {role} ({playersByRole[role].length})
                    </button>
                  </li>
                ))}
              </ul>

              {/* Tab Content */}
              <div className="tab-content">
                {/* All Players */}
                <div className="tab-pane fade show active" id="all-players">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Player</th>
                          <th>Role</th>
                          <th>Base Price</th>
                          <th>Sold Price</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {players.map((player, index) => (
                          <tr key={player.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                
                                  <img
                                    src={player.image || "/player.png"}
                                    alt={player.name}
                                    className="rounded-circle me-2"
                                    style={{ width: "40px", height: "40px", objectFit: "cover" }}
                                  />
                               
                                <div>
                                  <strong>{player.name}</strong>
                                  {player.isCaptain && (
                                    <span className="badge bg-warning text-dark ms-2">
                                      <i className="bi bi-star-fill me-1"></i>Captain
                                    </span>
                                  )}
                                  {player.playerNumber && (
                                    <small className="text-muted d-block">#{player.playerNumber}</small>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-info text-dark">{player.role}</span>
                            </td>
                            <td>৳{player.price?.toLocaleString() || 0}</td>
                            <td className="fw-bold text-success">
                              ৳{player.soldPrice?.toLocaleString() || player.price?.toLocaleString() || 0}
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEditPlayer(player)}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Players by Role */}
                {Object.entries(playersByRole).map(([role, rolePlayers]) => (
                  <div 
                    className="tab-pane fade" 
                    id={`role-${role.replace(/\s+/g, '-')}`} 
                    key={role}
                  >
                    <div className="row">
                      {rolePlayers.map((player) => (
                        <div key={player.id} className="col-md-4 mb-3">
                          <div className="card h-100">
                            {player.image && (
                              <img
                                src={player.image}
                                className="card-img-top"
                                alt={player.name}
                                style={{ height: "200px", objectFit: "cover" }}
                              />
                            )}
                            <div className="card-body">
                              <h5 className="card-title">
                                {player.name}
                                {player.isCaptain && (
                                  <span className="badge bg-warning text-dark ms-2">
                                    <i className="bi bi-star-fill"></i>
                                  </span>
                                )}
                              </h5>
                              {player.playerNumber && (
                                <p className="card-text text-muted">#{player.playerNumber}</p>
                              )}
                              <p className="card-text">
                                <strong>Bought for:</strong>{" "}
                                <span className="text-success">
                                  ৳{player.soldPrice?.toLocaleString() || player.price?.toLocaleString() || 0}
                                </span>
                              </p>
                              {player.mobileNo && (
                                <p className="card-text small">
                                  <i className="bi bi-telephone me-1"></i>
                                  {player.mobileNo}
                                </p>
                              )}
                              <button
                                className="btn btn-sm btn-outline-primary w-100"
                                onClick={() => handleEditPlayer(player)}
                              >
                                <i className="bi bi-pencil me-1"></i>
                                Edit Player
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit Team Component */}
      {showEditTeam && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "20px",
          }}
        >
          <EditTeamDetails
            gameId={gameId}
            teamId={teamId}
            team={team}
            onClose={handleCloseEditTeam}
          />
        </div>
      )}

      {/* Edit Player Component */}
      {editingPlayer && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "20px",
          }}
        >
          <EditTeamPlayer
            gameId={gameId}
            teamId={teamId}
            playerId={editingPlayerId}
            player={editingPlayer}
            players={players}
            onClose={handleCloseEditPlayer}
          />
        </div>
      )}

      <Popup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ isOpen: false, message: '', title: '', type: 'info' })}
        message={popup.message}
        title={popup.title}
        type={popup.type}
      />

      <Confirm
        isOpen={confirm.isOpen}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ isOpen: false, message: '', title: '', onConfirm: null })}
        message={confirm.message}
        title={confirm.title}
        type={confirm.type}
      />
    </div>
  );
};

export default TeamDetails;